import type {AiChatVideoActionSource} from '@Libraries/ga-events';
import {AiChatTurnToVideoPublishType, GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {PublishingOption} from '@Components/ai-chat-window/components/publishing-options/publishing-options.types';
import type {LoadingStates} from '@Utils/loading.util';
import {getPosterHashedIdForAiChatAnimationAsset} from '@Components/ai-chat-window/lib/chat-attachment-image.library';
import {runPublishFlow} from '@Components/ai-chat-window/lib/publish-flow.library';
import {AiChatAssetKind} from '@Components/ai-chat-window/lib/publish-flow.types';
import {useAiChatActions} from '@AiChat/providers/ai-chat.context';

interface UsePublishChatAnimation {
  publishToSocialMedia: () => Promise<void>;
  promoteAsEvent: () => Promise<void>;
  publishAsEmailCampaign: () => Promise<void>;
}

const GA4_TYPE_BY_OPTION: Record<PublishingOption, AiChatTurnToVideoPublishType> = {
  [PublishingOption.PUBLISH_TO_SOCIAL_MEDIA]: AiChatTurnToVideoPublishType.SOCIAL_MEDIA,
  [PublishingOption.PROMOTE_AS_EVENT]: AiChatTurnToVideoPublishType.EVENT,
  [PublishingOption.PUBLISH_AS_EMAIL_CAMPAIGN]: AiChatTurnToVideoPublishType.EMAIL,
};

export const usePublishChatAnimation = (chatId: string, animationHashedId: string, source: AiChatVideoActionSource): UsePublishChatAnimation => {
  const actions = useAiChatActions();

  const updateLoadingPublishingOption = (loadingOption: PublishingOption | null, loadingState: LoadingStates): void => {
    actions.updateLoadingStatesForAnimationActions(chatId, animationHashedId, {
      loadingPublishingOption: loadingOption,
      publishingLoadingState: loadingState,
    });
  };

  const publish = async (option: PublishingOption): Promise<void> => {
    await runPublishFlow({
      option,
      assetKind: AiChatAssetKind.ANIMATION,
      hashedId: animationHashedId,
      trackAnalytics: (): void => {
        window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_TURNTOVIDEO_PUBLISH, {
          [GA4EventParamName.TYPE]: GA4_TYPE_BY_OPTION[option],
          [GA4EventParamName.SOURCE]: source,
        });
      },
      fetchPosterHashedId: (): Promise<string> => {
        return getPosterHashedIdForAiChatAnimationAsset(animationHashedId);
      },
      setLoadingState: updateLoadingPublishingOption,
    });
  };

  return {
    publishToSocialMedia: (): Promise<void> => {
      return publish(PublishingOption.PUBLISH_TO_SOCIAL_MEDIA);
    },
    promoteAsEvent: (): Promise<void> => {
      return publish(PublishingOption.PROMOTE_AS_EVENT);
    },
    publishAsEmailCampaign: (): Promise<void> => {
      return publish(PublishingOption.PUBLISH_AS_EMAIL_CAMPAIGN);
    },
  };
};
